import { useState } from 'react';
import { LoadingSpinner } from '../layout/LoadingSpinner';
import type { Education, CreateEducationRequest } from '../../types/profile';

interface EducationFormProps {
  education?: Education | null;
  onSubmit: (data: CreateEducationRequest) => Promise<void>;
  onCancel: () => void;
}

export function EducationForm({ education, onSubmit, onCancel }: EducationFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    institution: education?.institution || '',
    degree: education?.degree || '',
    field: education?.field || '',
    startDate: education?.startDate ? education.startDate.split('T')[0] : '',
    endDate: education?.endDate ? education.endDate.split('T')[0] : '',
    current: education?.current || false,
    gpa: education?.gpa || '',
    description: education?.description || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
  };

  const handleCurrentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData(prev => ({
      ...prev,
      current: checked,
      endDate: checked ? '' : prev.endDate
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.institution.trim()) {
      setError('Institution name is required');
      return;
    }
    
    if (!formData.degree.trim()) {
      setError('Degree is required');
      return;
    }
    
    if (!formData.startDate) {
      setError('Start date is required');
      return;
    }
    
    if (!formData.current && !formData.endDate) {
      setError('End date is required for completed education');
      return;
    }
    
    if (!formData.current && formData.endDate && formData.startDate > formData.endDate) {
      setError('End date must be after start date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData: CreateEducationRequest = {
        institution: formData.institution.trim(),
        degree: formData.degree.trim(),
        field: formData.field.trim() || undefined,
        startDate: formData.startDate,
        endDate: formData.current ? undefined : formData.endDate,
        current: formData.current,
        gpa: formData.gpa.trim() || undefined,
        description: formData.description.trim() || undefined,
      };

      await onSubmit(submitData);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to save education');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {education ? 'Edit Education' : 'Add Education'}
        </h3>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
            Institution *
          </label>
          <input
            type="text"
            id="institution"
            name="institution"
            value={formData.institution}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="University or school name"
          />
        </div>
        <div>
          <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-1">
            Degree *
          </label>
          <input
            type="text"
            id="degree"
            name="degree"
            value={formData.degree}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., Bachelor of Science, Master's, PhD"
          />
        </div>
      </div>

      <div>
        <label htmlFor="field" className="block text-sm font-medium text-gray-700 mb-1">
          Field of Study
        </label>
        <input
          type="text"
          id="field"
          name="field"
          value={formData.field}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., Computer Science, Business Administration"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date *
          </label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date {!formData.current && '*'}
          </label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={formData.endDate}
            onChange={handleInputChange}
            disabled={formData.current}
            required={!formData.current}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>
        <div>
          <label htmlFor="gpa" className="block text-sm font-medium text-gray-700 mb-1">
            GPA
          </label>
          <input
            type="text"
            id="gpa"
            name="gpa"
            value={formData.gpa}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., 3.8/4.0"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="current"
          name="current"
          checked={formData.current}
          onChange={handleCurrentChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="current" className="ml-2 block text-sm text-gray-700">
          I am currently studying here
        </label>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Relevant coursework, achievements, activities..."
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
        >
          {loading && <div className="mr-2"><LoadingSpinner size="sm" /></div>}
          {loading ? 'Saving...' : (education ? 'Update' : 'Add')} Education
        </button>
      </div>
    </form>
  );
}